import { Box, Modal, type PropsWithBox } from "@saleor/macaw-ui-next";
import { type ReactNode } from "react";

export type ContentSize = "xs" | "sm" | "md" | "lg" | "xl";

type ContentProps = PropsWithBox<{
  children: ReactNode;
  disableAutofocus?: boolean;
  disableEscapeKeyDown?: boolean;
  size: ContentSize;
}>;

const sizes: Record<ContentSize, number> = {
  xs: 444,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

export const Content = ({
  children,
  disableAutofocus,
  disableEscapeKeyDown,
  size,
  ...rest
}: ContentProps) => {
  return (
    <Modal.Content
      disableAutofocus={disableAutofocus}
      dialogContentProps={{
        onPointerDownOutside: e => {
          // This fixes issues when cursor was clicked on DataGrid x/y coordinates
          // For example: when in modal clicked on "View metadata" button in DataGrid
          e.detail.originalEvent.preventDefault();
        },
        onInteractOutside: e => {
          // Prevent modal from closing when interacting with popovers (e.g., filter dropdowns)
          // Popovers render in portals outside the modal's DOM tree, so Radix treats them as "outside"
          const target = e.target as HTMLElement;

          if (target?.closest("[data-radix-popper-content-wrapper]")) {
            e.preventDefault();
          }
        },
        onEscapeKeyDown: disableEscapeKeyDown ? e => e.preventDefault() : undefined,
      }}
    >
      <Box
        // 패널은 불투명한 흰색으로 둔다. 유리는 뒤의 가림막이 맡는다. (2026-09-21 KST 수정)
        //
        // 한때 이 판도 반투명(0.8)으로 뒀는데, 가림막이 페이지를 #adadad 로 깔아 둔 위라
        // 판이 #e4e4e4 로 앉았다. macaw 입력칸은 흰 바탕 위 가느다란 테두리 하나로만
        // 구분되게 생겼고 그 테두리가 #e8e8e8 이라, 판이 조금만 어두워져도 입력칸이
        // 배경에 묻힌다. 실제로 셀렉트 안쪽이 판과 완전히 같은 색이 됐다.
        backgroundColor="default1"
        boxShadow="defaultModal"
        borderRadius={4}
        position="fixed"
        __left="50%"
        __top="50%"
        __transform="translate(-50%, -50%)"
        borderStyle="solid"
        borderWidth={1}
        borderColor="default1"
        padding={6}
        __maxHeight="calc(100vh - 100px)"
        __width="calc(100% - 64px)"
        display="grid"
        gap={6}
        __maxWidth={sizes[size]}
        overflowX="hidden"
        overflowY="auto"
        {...rest}
      >
        {children}
      </Box>
    </Modal.Content>
  );
};
